import { useState, useEffect, useCallback, useMemo } from "react";
import {
    Box, Typography, Button, Chip, IconButton, Tabs, Tab, TextField, MenuItem, Tooltip, Paper,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import CloseIcon from "@mui/icons-material/Close";
import { getReservations, cancelReservation, markNoShow } from "./ReservationApi";
import ReservationFormDialog from "./ReservationFormDialog";
import CheckInDialog from "./ReservationCheckInDialog";
import { formatDateText } from "./CommonFunctions";

const STATUS_COLOR = {
    reserved: "warning",
    converted: "success",
    cancelled: "default",
    no_show: "error",
};

const STATUS_LABEL = {
    reserved: "Reserved",
    converted: "Checked in",
    cancelled: "Cancelled",
    no_show: "No-show",
};

function todayISO() {
    return new Date().toISOString().slice(0, 10);
}
function addDays(iso, n) {
    const d = new Date(iso);
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
}

export default function Reservations() {
    const [tab, setTab] = useState("arrivals"); // arrivals | all
    const [reservations, setReservations] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");
    const [formOpen, setFormOpen] = useState(false);
    const [checkInTarget, setCheckInTarget] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(() => {
        const filters =
            tab === "arrivals"
                ? { status: "reserved", toDate: addDays(todayISO(), 2) }
                : { status: statusFilter || undefined, search: search || undefined };
        setLoading(true);
        getReservations(filters)
            .then(setReservations)
            .catch(() => setReservations([]))
            .finally(() => setLoading(false));
    }, [tab, statusFilter, search]);

    useEffect(() => { load(); }, [load]);

    async function handleCancel(id) {
        await cancelReservation(id, {});
        load();
    }
    async function handleNoShow(id) {
        await markNoShow(id, {});
        load();
    }

    // DataGrid wants flat, precomputed values per row rather than derived-at-render helpers
    const rows = useMemo(
        () =>
            reservations.map((r) => {
                const c = r.customers?.[0] || r.Customers?.[0];
                const room = r.Room || r.room;
                return {
                    id: r.id,
                    guestName: c ? `${c.fname || ""} ${c.lname || ""}`.trim() || "—" : "—",
                    roomLabel: r.roomId && room ? `Room ${room.alias}` : (r.roomId ? `Room #${r.roomId}` : "Unassigned"),
                    requestedCheckin: r.requestedCheckin,
                    requestedCheckout: r.requestedCheckout,
                    status: r.status,
                };
            }),
        [reservations]
    );

    const columns = useMemo(
        () => [
            { field: "guestName", headerName: "Guest", flex: 1, minWidth: 160 },
            { field: "roomLabel", headerName: "Room", flex: 1, minWidth: 130 },
            { field: "requestedCheckin", headerName: "Check-in", flex: 1, minWidth: 120,
                renderCell: (params) => (
                        <Typography variant="body">{formatDateText(params.value)}</Typography>
                      ),
             },
            { field: "requestedCheckout", headerName: "Check-out", flex: 1, minWidth: 120,
                renderCell: (params) => (
                        <Typography variant="body">{formatDateText(params.value)}</Typography>
                      ),
             },
            {
                field: "status",
                headerName: "Status",
                minWidth: 140,
                renderCell: (params) => (
                    <Chip size="small" label={STATUS_LABEL[params.value]} color={STATUS_COLOR[params.value]} />
                ),
            },
            {
                field: "actions",
                headerName: "Actions",
                minWidth: 150,
                sortable: false,
                filterable: false,
                renderCell: (params) => {
                    if (params.row.status !== "reserved") return null;
                    return (
                        <>
                            <Tooltip title="Check in">
                                <IconButton
                                    size="small" color="success"
                                    onClick={() => setCheckInTarget(reservations.find((r) => r.id === params.row.id))}
                                >
                                    <CheckIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark no-show">
                                <IconButton size="small" color="error" onClick={() => handleNoShow(params.row.id)}>
                                    <PersonOffIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel">
                                <IconButton size="small" onClick={() => handleCancel(params.row.id)}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    );
                },
            },
        ],
        [reservations]
    );

    return (
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h5" fontWeight={600}>Reservations</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
                    New reservation
                </Button>
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab value="arrivals" label="Upcoming arrivals" />
                <Tab value="all" label="All reservations" />
            </Tabs>

            {tab === "all" && (
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <TextField
                        size="small" label="Search guest" value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <TextField
                        size="small" select label="Status" value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        sx={{ minWidth: 160 }}
                    >
                        <MenuItem value="">All statuses</MenuItem>
                        <MenuItem value="reserved">Reserved</MenuItem>
                        <MenuItem value="converted">Checked in</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                        <MenuItem value="no_show">No-show</MenuItem>
                    </TextField>
                </Box>
            )}

            <Paper variant="outlined" sx={{ height: 520 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    disableRowSelectionOnClick
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    sx={{ border: 0 }}
                />
            </Paper>

            <ReservationFormDialog
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onCreated={load}
            />
            <CheckInDialog
                open={!!checkInTarget}
                reservation={checkInTarget}
                onClose={() => setCheckInTarget(null)}
                onCheckedIn={load}
            />
        </Box>
    );
}